<template>
   <div class="h-24"></div>
   <div class="fixed bottom-0 left-0 z-50 w-full border-t border-grey-10 bg-white py-4 lg:py-6">
      <div class="mx-5">
         <div class="mx-auto max-w-md">
            <DefaultButton
               v-show="isBackButtonDisplayed"
               data-cy="back-submit-btn"
               class="float-left"
               roundedEdges="md"
               @click="$emit('previous-click')"
            >
               {{ $t("survey.button_back") }}
            </DefaultButton>

            <!-- next button -->
            <LoadingButton
               v-show="isNextButtonDisplayed"
               data-cy="next-submit-btn"
               class="float-right"
               roundedEdges="md"
               :isDisabled="!isNextBtnEnabled"
               :isLoading="state.isSubmittingAnswer"
               :text="nextBtnText"
               @click="nextButtonClicked()"
            />
         </div>
      </div>
   </div>
</template>

<script setup lang="ts">
import { inject } from "vue";
import { useI18n } from "@/vue-i18n/vue-i18n";
import { useRouter } from "vue-router";
import { useToastKey } from "@/plugins/useToast";
import AnswersAPI, { type AnswerResp } from "@/data/api-layer/answersApi";
import { useFlowParticipationStore } from "@/stores/flowParticipationStore";

interface Props {
   currentBlock: any;
   currentAnswer: any;
   isAnswerValid: boolean;
   isBackButtonDisplayed: boolean;
   isNextButtonDisplayed: boolean;
   nextBtnText?: string;
   nextCalledFromComponent: number;
}

const emit = defineEmits(["answer-submitted", "previous-click", "show-errors", "participation-reset"]);
const $router = useRouter();
const { t: $t } = useI18n();
const useToast = inject(useToastKey);
const flowParticipationStore = useFlowParticipationStore();

const props = withDefaults(defineProps<Props>(), { nextBtnText: "default" });

// state
const state = reactive({
   isSubmittingAnswer: false,
});

// lifecycle
// -----------------------------------------
watch(
   () => props.nextCalledFromComponent,
   (val, newVal) => {
      // The value of nextCalledFromComponent will increment by 1 each time enter is pressed in a situation which should trigger an answer submission
      if (val && val !== newVal) nextButtonClicked();
   }
);

// Computed
// -----------------------------------------
/** Some components have custom next button text if the user hasn't submitted their answer yet */
const nextBtnText = computed(() => {
   if (props.nextBtnText === "confirm_kit_order") {
      return $t(`survey.next.button_confirm_kit_order`);
   } else if (props.nextBtnText === "consent") {
      return $t(`survey.next.button_consent`);
   } else if (props.nextBtnText === "submit") {
      return $t(`survey.next.button_submit`);
   }
   // default text or anything else is Next button
   else {
      return $t(`survey.next.button_default`);
   }
});

const isNextBtnEnabled = computed(() => {
   // if we are submitting an answer, disable the next button
   return !state.isSubmittingAnswer;
});

// Methods
// -----------------------------------------
/** Next was clicked, we allow the user to go forward only if input is valid and answer was submitted successfully */
async function nextButtonClicked() {
   // used for the "loading" state of the next button
   state.isSubmittingAnswer = true;

   // We can only move forward if the page is valid
   if (!props.isAnswerValid) {
      emit("show-errors");
      state.isSubmittingAnswer = false;
      console.info("Cannot move forward because the current block is not valid");
      return;
   }

   // submit the answer and raise any errors if the answer was not submitted successfully
   // console.log("submitting", props.currentBlock?.blockVersionId, props.currentAnswer, props.currentBlock?.id);
   // In the case of an informational block, the answer will be { data: null }
   // However it is still submitted to the BE and information about where to go next is returned
   try {
      let response: AnswerResp;

      // There are 2 separate requests for submitting answers and they depend on the presence of token
      // Token is used to authorise users in anonymous public flows
      // This logic (to determine if token is present) can't be a part of the service layer, because we want to keep it service generic so it can be auto-generated by OpenAPI in the future
      if (flowParticipationStore.token) {
         response = await AnswersAPI.submitWithToken(
            props.currentBlock?.blockVersionId,
            props.currentAnswer,
            flowParticipationStore.token
         );
      } else {
         response = await AnswersAPI.submit(props.currentBlock?.blockVersionId, props.currentAnswer);
      }

      // If we have submitted the answer successfully, emit the response
      emit("answer-submitted", response);
      state.isSubmittingAnswer = false;
   } catch (e: any) {
      console.error(`Failed submitting answer for block version id: ${props.currentBlock?.id}`, " - Response: ", e);

      if (e?.response?.status === 422 && e?.response?.data?.error_code === "flow_already_completed") {
         // User has already completed the flow > reload the page to show the flow completed page
         $router.go(0); // 0 because TS requires a parameter
      } else if (e?.response?.status === 400 && e?.response?.data?.error_code === "participation_reset") {
         // This flow has been updated while the participant is completing it
         emit("participation-reset");
      } else {
         // for any other error submitting the answer, we enable the next button again
         // and show a hint about resubmitting or getting in touch if this continues to be a problem
         useToast({ text: $t("survey.errors.answer_submission_error"), type: "error", position: "center" });
      }

      state.isSubmittingAnswer = false;
      return;
   }
}
</script>
